import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Popconfirm,
  message,
  Spin,
  Modal,
  Form,
  InputNumber,
  Select,
} from "antd";
import api from "../services/api";

const { Option } = Select;

const PlayerStats = () => {
  const [stats, setStats] = useState([]);
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [innings, setInnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStat, setEditingStat] = useState(null);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, playersRes, matchesRes, inningsRes] = await Promise.all([
        api.get("/player-stats"),
        api.get("/players"),
        api.get("/matches"),
        api.get("/innings"),
      ]);

      setStats(statsRes.data?.data || statsRes.data || []);
      setPlayers(playersRes.data?.data || playersRes.data || []);
      setMatches(matchesRes.data?.data || matchesRes.data || []);
      setInnings(inningsRes.data?.data || inningsRes.data || []);
    } catch (error) {
      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (values) => {
    try {
      if (editingStat) {
        await api.put(`/player-stats/${editingStat.id}`, values);
        message.success("Player stats updated successfully");
      } else {
        await api.post("/player-stats", values);
        message.success("Player stats added successfully");
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchData();
    } catch (error) {
      message.error("Failed to save player stats");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/player-stats/${id}`);
      message.success("Deleted successfully");
      fetchData();
    } catch {
      message.error("Failed to delete");
    }
  };

  const columns = [
    {
      title: "Player",
      dataIndex: "player",
      render: (player) => player?.name || "N/A",
      ellipsis: true,
    },
    {
      title: "Match",
      dataIndex: "match",
      render: (match) => match?.match_name || `Match ${match?.id}`,
      ellipsis: true,
    },
    {
      title: "Inning",
      dataIndex: "inning",
      render: (inning) => inning?.inning_number,
      ellipsis: true,
    },
    { title: "Runs", dataIndex: "runs" },
    { title: "Balls", dataIndex: "balls_faced" },
    { title: "4s", dataIndex: "fours" },
    { title: "6s", dataIndex: "sixes" },
    { title: "Overs", dataIndex: "overs_bowled" },
    { title: "Conceded", dataIndex: "runs_conceded" },
    { title: "Wkts", dataIndex: "wickets" },
    {
      title: "Out?",
      dataIndex: "is_out",
      render: (v) => (v ? "Yes" : "No"),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Button
            type="link"
            onClick={() => {
              setEditingStat(record);
              form.setFieldsValue({
                ...record,
                player_id: record.player?.player_id,
                match_id: record.match?.id,
                inning_id: record.inning?.id,
              });
              setIsModalVisible(true);
            }}
          >
            Edit
          </Button>

          <Popconfirm
            title="Are you sure?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: 10 }}>
      {/* Top Bar - responsive */}
      <div
        style={{
          display: "flex",
          flexDirection: window.innerWidth < 600 ? "column" : "row",
          gap: 10,
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0 }}>Player Match Stats</h2>

        <Button
          type="primary"
          style={{ width: window.innerWidth < 600 ? "100%" : "auto" }}
          onClick={() => {
            setEditingStat(null);
            form.resetFields();
            setIsModalVisible(true);
          }}
        >
          Add Player Stats
        </Button>
      </div>

      {loading ? (
        <Spin />
      ) : (
        <Table
          columns={columns}
          dataSource={stats}
          rowKey="id"
          bordered
          scroll={{ x: 1000 }} // Mobile scroll
          size="small" // compact view for mobile
        />
      )}

      <Modal
        title={editingStat ? "Edit Player Stats" : "Add Player Stats"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        okText="Save"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="player_id" label="Player" rules={[{ required: true }]}>
            <Select placeholder="Select player" style={{ width: "100%" }}>
              {players.map((p) => (
                <Option key={p.player_id} value={p.player_id}>
                  {p.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="match_id" label="Match" rules={[{ required: true }]}>
            <Select placeholder="Select match" style={{ width: "100%" }}>
              {matches.map((m) => (
                <Option key={m.id} value={m.id}>
                  {m.match_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="inning_id"
            label="Inning"
            rules={[{ required: true }]}
          >
            <Select placeholder="Select inning" style={{ width: "100%" }}>
              {innings.map((i) => (
                <Option key={i.id} value={i.id}>
                  Inning {i.inning_number}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="runs" label="Runs">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="balls_faced" label="Balls Faced">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="fours" label="Fours">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="sixes" label="Sixes">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="overs_bowled" label="Overs Bowled">
            <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="runs_conceded" label="Runs Conceded">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="wickets" label="Wickets">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="is_out" label="Out?">
            <Select style={{ width: "100%" }}>
              <Option value={true}>Yes</Option>
              <Option value={false}>No</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PlayerStats;
